import { SecureKeys, type NativeContext } from '@heviton/bridge-protocol';
import type { RawSessionNode } from '@heviton/heviton-api-app-legacy';
import { env } from '@/env';
import { isNative } from './client';

declare global {
  interface Window {
    __NATIVE__?: NativeContext;
  }
}

/**
 * native 환경에서 주입된 `window.__NATIVE__` 반환. WebView 가 아닌 환경에선 null.
 * native 는 WebView content load 전 이 객체를 박아두므로 동기 접근 가능.
 */
export function getNativeContext(): NativeContext | null {
  if (typeof window === 'undefined') return null;
  return window.__NATIVE__ ?? null;
}

/**
 * 데스크톱 브라우저용 terminalId — localStorage 영속. SecureKeys.terminalId 키 공유 (SSOT).
 * 시크릿 모드 / 캐시 삭제 시 새로 생성됨. BE 가 (c) refresh-binding 정책이면
 * 이 경우 강제 logout 후 재로그인 필요 (정상 UX).
 */
function getOrCreateLocalTerminalId(): string {
  try {
    const existing = localStorage.getItem(SecureKeys.terminalId);
    if (existing) return existing;
    const generated = crypto.randomUUID();
    localStorage.setItem(SecureKeys.terminalId, generated);
    return generated;
  } catch {
    // localStorage 차단 환경 (드물지만 사파리 시크릿 모드 등). 메모리 생성·매 호출 새 UUID.
    return crypto.randomUUID();
  }
}

/**
 * 현재 환경에 맞는 terminalId 반환.
 *   - native: `__NATIVE__.terminalId` (앱 sandbox 안 SecureStore 영속)
 *   - web: localStorage UUID
 */
export function getTerminalId(): string {
  if (isNative()) {
    const ctx = getNativeContext();
    if (ctx?.terminalId) return ctx.terminalId;
    // 이론상 발생 안 함 — native 는 항상 inject. 안전 fallback.
    console.warn('[bridge/native-context] native env without terminalId in __NATIVE__');
  }
  return getOrCreateLocalTerminalId();
}

/**
 * BE `session.app.type` 화이트리스트 (D16). native 면 `__NATIVE__.platform`, web 이면 `'web'`.
 */
export function getAppType(): 'web' | 'ios' | 'android' {
  const ctx = getNativeContext();
  return ctx?.platform ?? 'web';
}

/**
 * 웹 번들 버전 — `env.appVersion` (`environments/<mode>.ts` 의 `computed` 에서 `apps/web/package.json`
 * `version` 자동 주입). 실행 환경과 무관하게 항상 이 값 — native 셸 안에서도 OTA 로 갈린 웹 버전을
 * 따로 봐야 하므로 앱 버전과 별개 축이다.
 */
export function getWebVersion(): string {
  return env.appVersion;
}

/**
 * native 앱(셸) 버전 — `__NATIVE__.appVersion`. web 실행 시엔 해당 축이 없어 null.
 * **semver 원문만** — `getAppVersion()` 을 거쳐 BE `session.app.version` 과 버전 게이트 비교에
 * 그대로 쓰이므로 표시용 가공을 섞지 않는다(빌드 넘버는 `getNativeBuildNumber()` 로 별도).
 */
export function getNativeAppVersion(): string | null {
  return getNativeContext()?.appVersion ?? null;
}

/**
 * native 빌드 넘버(iOS `CFBundleVersion` / Android `versionCode`) — EAS remote 카운터가 매 빌드 증가시킨다.
 * 같은 semver 로 여러 번 빌드되므로 CS 대응 시 실제 식별자는 `버전 + 빌드넘버` 조합이다. web 실행이면 null.
 */
export function getNativeBuildNumber(): string | null {
  return getNativeContext()?.buildNumber ?? null;
}

/**
 * EAS Update 채널(`development` | `preview` | `production`). **preview 빌드 식별의 유일한 단서** —
 * preview 프로필은 env·번들 ID·웹 빌드 mode 를 production 과 공유해 다른 값으로는 구분이 불가능하다.
 * web 실행이거나 EAS Update 미구성(dev client) 이면 null.
 */
export function getNativeChannel(): string | null {
  return getNativeContext()?.updateChannel ?? null;
}

/**
 * BE `session.app.version`. native 면 mobile 앱 버전, web 이면 웹 번들 버전 —
 * `getAppType()` 이 고른 플랫폼 축과 짝을 이룬다(버전 게이트도 같은 축으로 판정).
 */
export function getAppVersion(): string {
  return getNativeAppVersion() ?? getWebVersion();
}

/**
 * BE 화이트리스트의 클라이언트 식별자 (D17). 현재 heviton-user-app 단일.
 */
const APP_CODE = 'HEVITON_USER';

/**
 * `session` payload SSOT (login / refresh / logout 공용). D15~D18 정합.
 * FCM 토큰은 login body 에 싣지 않는다 — 전용 엔드포인트(`POST /api/v3/auth/fcm`)로
 * 토큰 변경/인증 시점에 별도 등록(app/runtime `fcm-sync`). 관련 ADR: push-notifications.
 */
export function getSessionNode(): RawSessionNode {
  return {
    terminalId: getTerminalId(),
    app: {
      code: APP_CODE,
      type: getAppType(),
      version: getAppVersion(),
      fcmToken: '',
    },
  };
}
